import {
  faChevronRight,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { Box, Flex, Loader, Radio, Text } from '@mantine/core'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetAllItems } from '@flecto/core/saas'
import { useQueryGetUserTaxProfile } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type ParameterID, type CoreItem } from '@flecto/types'
import {
  AvatarItem,
  InputSearch,
  Modal,
  Result,
  TextItemCategory,
} from '@flecto/ui'
import { Icon } from '@flecto/ui'

import { TextItemPrice } from '../../../items'
import styles from './styles.module.css'

export type ModalProductSelectProps = {
  setItem: (field: string, value: CoreItem) => void
  setProductInitialValues: (value: Partial<CoreItem>) => void
}

export function ModalProductSelect({
  setItem,
  setProductInitialValues,
}: ModalProductSelectProps) {
  const track = useAnalytics('segment')
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const [items, setItems] = useState<CoreItem[]>([])
  const [itemSelected, setItemSelected] = useState<CoreItem | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isFetched, isLoading } = useQueryGetAllItems({
    limit: 10000,
    q: searchTerm,
    insurable: true,
  })

  const { isLoading: isLoadingTaxProfile } = useQueryGetUserTaxProfile()

  const processedItems = useMemo(() => {
    if (!isFetched) return []

    return data?.item.map((item) => ({
      ...item,
    }))
  }, [data, isFetched])

  const groupedItems = useMemo(
    () =>
      items.reduce(
        (map, item) => {
          if (!item.category_id) return map
          if (!map[item.category_id]) map[item.category_id] = []
          map[item.category_id].push(item)
          return map
        },
        {} as Record<ParameterID, CoreItem[]>
      ),
    [items]
  )

  useEffect(() => {
    if (isFetched) {
      setItems(processedItems as CoreItem[])
    }
  }, [data, isFetched])

  return (
    <Modal size='xxl'>
      <Modal.Close />
      <Modal.Title>
        {t('create_insurance.select_product.modal_title')}
        {searchTerm && (
          <>
            <Icon icon={faChevronRight} size='md' ml='xs' color='dimmed' />
            <Text size='xl' fw={500}>
              {searchTerm}
            </Text>
          </>
        )}
      </Modal.Title>

      <Modal.Content>
        <InputSearch
          placeholder={t(
            'create_insurance.select_product.modal_search_placeholder'
          )}
          onChange={(value) => setSearchTerm(value)}
        />

        <Text c='dimmed' size='xs' my='lg' px='md' fw={500}>
          {t(
            'create_insurance.select_product.modal_showing_elegible_products',
            {
              quantity: items.length,
            }
          )}
        </Text>

        {isLoading || isLoadingTaxProfile ? (
          <Flex align='center' justify='center' style={{ overflow: 'hidden' }}>
            <Loader size='md' />
          </Flex>
        ) : items.length <= 0 ? (
          <Result variant='info'>
            <Result.Title>
              {t('create_insurance.select_product.modal_no_results_title', {
                search: searchTerm,
              })}
            </Result.Title>
            <Result.Description>
              {t(
                'create_insurance.select_product.modal_no_results_description'
              )}
            </Result.Description>
          </Result>
        ) : (
          <Flex
            direction='column'
            h={400}
            style={{ overflow: 'auto' }}
            pb='sm'
            pr='sm'
          >
            {Object.entries(groupedItems).map(([categoryId, categoryItems]) => (
              <Box key={categoryId}>
                <TextItemCategory
                  key={categoryId}
                  categoryId={categoryId}
                  withBreadcrumbNames
                  size='sm'
                  c='dimmed'
                  px='md'
                  py='xs'
                  fw={500}
                />
                {categoryItems.map((item) => (
                  <Flex
                    align='center'
                    gap='md'
                    justify='space-between'
                    h={58}
                    pl='md'
                    pr='xl'
                    key={item.id}
                    className={clsx(styles.itemLine, {
                      [styles.itemSelected]: item.id === itemSelected?.id,
                    })}
                    onClick={() => {
                      setItemSelected(item)
                    }}
                  >
                    <Flex align='center' gap='sm'>
                      <Radio
                        checked={item.id === itemSelected?.id}
                        onChange={() => setItemSelected(item)}
                        size='xs'
                      />
                      <AvatarItem size='sm' item={item} />
                      <Text>{item.title ?? '-'}</Text>
                      <Icon
                        icon={faShieldCheck}
                        size='xs'
                        color='green.5'
                        mt={-3}
                      />
                    </Flex>

                    <Flex gap='md'>
                      <TextItemPrice item={item} c='dimmed' />
                      <Text c='dimmed'>
                        {item?.allow_overbooking
                          ? t('create_insurance.label_unlimited_stock')
                          : t('create_insurance.label_limited_stock', {
                              quantity: item?.quantity,
                            })}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            ))}
          </Flex>
        )}
      </Modal.Content>
      <Modal.Cancel />
      <Modal.Submit
        disabled={!itemSelected}
        onClick={() => {
          setItem('item', itemSelected as CoreItem)
          setProductInitialValues(itemSelected as CoreItem)
          track('INSURANCE_FORM__ITEM_SET', { item_id: itemSelected?.id ?? '' })
        }}
        type='submit'
      >
        {tActions('save')}
      </Modal.Submit>
    </Modal>
  )
}
